import {Component, computed, DestroyRef, inject, OnInit, ViewChild} from '@angular/core';
import {  MatDialog } from '@angular/material/dialog';
import {  MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import {  MatTableDataSource } from '@angular/material/table';
import {ActivatedRoute, Router} from '@angular/router';
import { Profile } from '../model/profile.model';
import { ProfileVersion } from '../model/profileversion.model';
import { DataprofileService } from './../service/dataprofile.service';
import { EditDataprofileVersionComponent } from '../edit-dataprofile-version/edit-dataprofile-version.component';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, switchMap, timer, tap, takeWhile, distinctUntilChanged, BehaviorSubject, map, interval, startWith, filter, take } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import saveAs from 'file-saver';

export enum ProfileUpdateStatus {
  RUNNING = "RUNNING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

export interface ProfileUpdateStatusDTO {
  profileId: string;
  message: string;
  status: ProfileUpdateStatus;
}

export interface CsvExportStatus {
  status: 'READY' | 'IN_PROGRESS' | 'NOT_FOUND';
  message: string;
}

@Component({
  selector: 'app-edit-dataprofile',
  templateUrl: './edit-dataprofile.component.html',
  styleUrls: ['./edit-dataprofile.component.css'],
})
export class EditDataprofileComponent implements OnInit {
  profile: Profile= new Profile();
  profilelist: Profile[] = [];
  profileversionlist: ProfileVersion[] = [];
  profileversion: ProfileVersion;
  exportStatus: CsvExportStatus;

  displayedColumns = ['Number','Description','Data Providers','edit_icon'];
  aixmProfileVersionlist_dataSource = new MatTableDataSource<ProfileVersion>(this.profileversionlist);

  private _profileId = this.route.snapshot.params["id"];

  private _profileUpdateStatusReloader$ = new BehaviorSubject(true);

  private _destroyRef = inject(DestroyRef);

  private _snackbar = inject(MatSnackBar);

  profileUpdateStatus = ProfileUpdateStatus;

  profileUpdate = toSignal(
    this._profileUpdateStatusReloader$.asObservable().pipe(
      switchMap(() => {
        // Create a poll request every 5 seconds while the status is RUNNING
        return timer(0, 5000).pipe(
          switchMap(() => this.dataProfileService.profileFromCatalogStatus(this._profileId)),
          distinctUntilChanged((prev, curr) => prev.status === curr.status),
          tap((result: ProfileUpdateStatusDTO) => {
            const isFinished = [ProfileUpdateStatus.COMPLETED, ProfileUpdateStatus.FAILED].includes(result.status);
            const hasTask = this.dataProfileService.profileUpdateTaskCounter()?.[this._profileId] > 0;
           
            if (isFinished && hasTask) {
              this._snackbar.open(result.message, 'Close');
              this.getProfileVersions(this._profileId);
              // Reset the  task counter
              this.dataProfileService.profileUpdateTaskCounter.update(prev => ({...prev, [this._profileId]: 0}));
            }
          }),
          takeWhile((result: ProfileUpdateStatusDTO) => result.status === ProfileUpdateStatus.RUNNING, true)
        );
      })
    )
  );

  isDisabledState = computed(() => {
    const profileUpdate = this.profileUpdate();
    return profileUpdate?.status === ProfileUpdateStatus.RUNNING;
  })

  //sorting
  @ViewChild(MatSort) sort: MatSort;
  //pagination
  @ViewChild(MatPaginator) paginator: MatPaginator;

  /**
   * Set the sort after the view init since this component will
   * be able to query its view for the initialized sort.
   */
  ngAfterViewInit() {
    this.aixmProfileVersionlist_dataSource.sort = this.sort;
    this.aixmProfileVersionlist_dataSource.paginator = this.paginator;
  }
  
  //filter rows
  applyFilter(filterValue: string) {
    filterValue = filterValue.trim(); // Remove whitespace
    filterValue = filterValue.toLowerCase(); // MatTableDataSource defaults to lowercase matches
    this.aixmProfileVersionlist_dataSource.filter = filterValue;
  }

  constructor(private route: ActivatedRoute, 
    private router: Router, 
    private dataProfileService: DataprofileService,public dialog: MatDialog) { }

  ngOnInit() {
    const id = this.route.snapshot.params['id'];
    this.getProfile(id);
    this.getProfileVersions(id);
  }

  getProfile(id: string): void {
    this.dataProfileService.getProfile(id)
      .subscribe(profile => this.profile = profile);
  }

  getProfileVersions(id: string): void {
    this.dataProfileService.getProfileVersions(id)
      .subscribe(profileversionlist => this.aixmProfileVersionlist_dataSource.data = profileversionlist);
  } 

  clear(): void {
    this.ngOnInit();
  }

  editProfile(): void {
    this.dataProfileService.editProfile(this.profile.id,
      {id: this.profile.id, name: this.profile.name, description: this.profile.description} as Profile)
    .subscribe(newProfileId => {
      this.router.navigate(['/profile/id', newProfileId]);
    });
  }
  
  updateProfileFromCatalog() {
    this.dataProfileService
      .updateProfileFromCatalog(this._profileId)
      .pipe(
        tap((response: { message: string, status: string }) => {
          this._snackbar.open(response.message, 'Close');
          this.dataProfileService.profileUpdateTaskCounter.update(prev => ({...prev, [this._profileId]: 1}));
          this._profileUpdateStatusReloader$.next(true);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  downloadProfile() {
  // start the profile csv export
  this.dataProfileService.startProfileExport(this._profileId, true).pipe(
    switchMap(() =>
    interval(3000).pipe(
      startWith(0),
      switchMap(() =>
        this.dataProfileService.getProfileExportStatus(this._profileId)
      ),
      tap((response: CsvExportStatus) => {
        this.exportStatus = response;
      }),
      filter(response => response.status === 'READY'),
      take(1)
     )  
    ),
    switchMap(() =>
      this.dataProfileService.downloadProfileReport(this._profileId)
    ),
    takeUntilDestroyed(this._destroyRef)
  ).subscribe({
    next: blob => {
      saveAs(blob, `${this._profileId}.csv`);
    },
    error: err => {
      console.error(err);
    }
  });
}

}

